import { Direction } from '@angular/cdk/bidi';
import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import {
  FloatLabelType,
  MatFormFieldAppearance,
} from '@angular/material/form-field';
import { IconType } from '@ng-icons/core';
import { Observable, Subscription } from 'rxjs';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import {
  MOT_OVERLAY_PANEL_CLOSED,
  MOT_OVERLAY_PANEL_OPENED,
} from 'src/app/@shared/constants/overlay-panel-a11y.constants';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { focusMatOptionElement } from '../../utils/focus-mat-option.util';
import {
  attachMatOptionListPanelKeyboard,
  detachMatOptionListPanelKeyboard,
  MatOptionListPanelBinding,
} from '../../utils/mat-option-list-keyboard.util';

@Component({
    selector: 'mot-select',
    templateUrl: './mot-select.component.html',
    styleUrls: ['./mot-select.component.scss'],
    standalone: false
})
export class MotSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild(MatSelect) matSelect?: MatSelect;

  @ContentChild('triggerContent', { static: false }) triggerContent: ElementRef;

  @Output() onIconClick: EventEmitter<MouseEvent> =
    new EventEmitter<MouseEvent>();

  @Input() compareWith?: (o1: any, o2: any) => boolean = (o1, o2) => o1 === o2;
  @Input() customOptionTemplate!: TemplateRef<any>;
  @Input() options: Observable<SelectOption<any>[]>;
  @Input() displayErrors: boolean = true;
  @Input() floatLabelType: FloatLabelType = 'auto';
  @Input() label: string;
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() placeholder: string;
  @Input() value: string;
  @Input() type: string;
  @Input() icon: IconType;

  @Input() formControl: FormControl;
  @Input() hasTrigger: boolean = false;

  @Input() class: string;
  @Input() labelClass: string;
  @Input() selectClass: string;
  @Input() iconClass: string;
  @Input() textAlign: string = 'start';
  @Input() panelClass: string = ""
  /**
   * a11y: accessible name forwarded to the underlying `<mat-select>` as
   * `aria-label`. Required when the select is used without a visible
   * `[label]` (and therefore without a `<mat-label>` that Material can use to
   * compute `aria-labelledby`), so the combobox is announced with a
   * meaningful name instead of just its current value.
   */
  @Input() ariaLabel: string;

  /** Unique id for error message (aria-describedby link). */
  errorId = 'mot-select-err-' + Math.random().toString(36).slice(2, 11);

  direction: Direction;
  private languageSubscription: Subscription;
  private openedChangeSubscription?: Subscription;
  private optionsSubscription?: Subscription;
  private focusFirstOptionTimeouts: ReturnType<typeof setTimeout>[] = [];
  private panelKeyboardBinding: MatOptionListPanelBinding | null = null;

  resolvedOptions: SelectOption<any>[] = [];

  /** Stable id for aria-controls on the combobox trigger (mat-select panel). */
  readonly comboboxPanelId =
    'mot-select-panel-' + Math.random().toString(36).slice(2, 11);

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private languageService: LanguageService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Initialize direction with current language
    this.direction = this.languageService.getSelectedLanguage().direction;
  }

  ngOnInit() {
    if (this.ngControl) {
      this.formControl = this.ngControl.control as FormControl;
      if (this.value) {
        this.formControl.setValue(this.value);
      }
    }

    // Subscribe to language changes
    this.languageSubscription =
      this.languageService.selectedLanguage$.subscribe((language) => {
        this.direction = language.direction;
        // Update textAlign based on direction
        this.textAlign = this.direction === 'rtl' ? 'end' : 'start';
      });

    if (this.options) {
      this.subscribeToOptions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && !changes['options'].firstChange) {
      this.subscribeToOptions();
    }
  }

  private subscribeToOptions(): void {
    this.optionsSubscription?.unsubscribe();
    if (this.options) {
      this.optionsSubscription = this.options.subscribe((opts) => {
        this.resolvedOptions = opts ?? [];
        if (this.matSelect?.panelOpen) {
          this.scheduleFocusFirstSelectOption(0);
        }
      });
    } else {
      this.resolvedOptions = [];
    }
  }

  ngAfterViewInit(): void {
    if (!this.matSelect) {
      return;
    }
    this.openedChangeSubscription = this.matSelect.openedChange.subscribe(
      (opened) => {
        if (opened) {
          this.dispatchOverlayPanelOpened();
          this.scheduleFocusFirstSelectOption(0);
        } else {
          this.dispatchOverlayPanelClosed();
          this.clearFocusFirstOptionTimeouts();
          this.detachSelectPanelKeyboard();
        }
      }
    );
  }

  ngOnDestroy() {
    this.openedChangeSubscription?.unsubscribe();
    this.optionsSubscription?.unsubscribe();
    this.clearFocusFirstOptionTimeouts();
    this.detachSelectPanelKeyboard();
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  get isPanelOpen(): boolean {
    return !!this.matSelect?.panelOpen;
  }

  /** Id of the overlay listbox panel (Material suffix `-panel`). */
  get comboboxControlsId(): string | null {
    return this.isPanelOpen ? `${this.comboboxPanelId}-panel` : null;
  }

  /** Accessible name for the combobox trigger (mat-label + custom trigger). */
  get comboboxAriaLabel(): string | null {
    const explicit = this.ariaLabel?.trim();
    if (explicit) {
      return explicit;
    }
    const fromLabel = this.label?.trim();
    return fromLabel || null;
  }

  trackOption(index: number, opt: SelectOption<unknown>): string | number {
    const value = opt?.value as { stopcode?: string; stopid?: string } | null;
    return value?.stopid ?? value?.stopcode ?? opt?.label ?? index;
  }

  private dispatchOverlayPanelOpened(): void {
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent(MOT_OVERLAY_PANEL_OPENED));
    }
  }

  private dispatchOverlayPanelClosed(): void {
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent(MOT_OVERLAY_PANEL_CLOSED));
    }
  }

  private clearFocusFirstOptionTimeouts(): void {
    this.focusFirstOptionTimeouts.forEach((id) => clearTimeout(id));
    this.focusFirstOptionTimeouts = [];
  }

  private scheduleFocusFirstSelectOption(attempt: number): void {
    const delayMs = attempt === 0 ? 0 : 50;
    const id = setTimeout(() => {
      if (!this.matSelect?.panelOpen) {
        return;
      }
      const options = this.matSelect.options;
      if (!options?.length) {
        if (attempt < 12) {
          this.scheduleFocusFirstSelectOption(attempt + 1);
        }
        return;
      }
      this.focusFirstSelectOption();
    }, delayMs);
    this.focusFirstOptionTimeouts.push(id);
  }

  /**
   * a11y: highlight first option and move DOM focus into the panel so screen
   * readers enter the list on open (mobile TalkBack / VoiceOver).
   */
  private focusFirstSelectOption(): void {
    const options = this.matSelect?.options?.toArray() ?? [];
    const firstIndex = options.findIndex((o) => !o.disabled);
    if (firstIndex < 0) {
      return;
    }

    const selectInternals = this.matSelect as unknown as {
      _keyManager?: { setActiveItem: (index: number) => void };
      _scrollOptionIntoView?: (index: number) => void;
    };
    selectInternals._keyManager?.setActiveItem(firstIndex);
    selectInternals._scrollOptionIntoView?.(firstIndex);

    const trigger = this.matSelect?._elementRef?.nativeElement?.querySelector(
      '.mat-mdc-select-trigger, .mat-select-trigger'
    ) as HTMLElement | undefined;
    trigger?.setAttribute('aria-expanded', 'true');
    focusMatOptionElement(options[firstIndex]);
    this.attachSelectPanelKeyboard();
  }

  private getSelectPanelElement(): HTMLElement | null {
    const selectInternals = this.matSelect as unknown as {
      _overlayDir?: { overlayRef?: { overlayElement: HTMLElement } };
    };
    const fromOverlay =
      selectInternals._overlayDir?.overlayRef?.overlayElement ?? null;
    if (fromOverlay) {
      return fromOverlay;
    }
    if (typeof document === 'undefined') {
      return null;
    }
    return document.getElementById(`${this.comboboxPanelId}-panel`);
  }

  private attachSelectPanelKeyboard(): void {
    this.detachSelectPanelKeyboard();
    const panel = this.getSelectPanelElement();
    if (!panel || !this.matSelect) {
      return;
    }

    this.panelKeyboardBinding = attachMatOptionListPanelKeyboard(
      panel,
      () => this.matSelect?.options?.toArray() ?? [],
      (index) => {
        const selectInternals = this.matSelect as unknown as {
          _keyManager?: { setActiveItem: (index: number) => void };
          _scrollOptionIntoView?: (index: number) => void;
        };
        selectInternals._keyManager?.setActiveItem(index);
        selectInternals._scrollOptionIntoView?.(index);
      }
    );
  }

  private detachSelectPanelKeyboard(): void {
    detachMatOptionListPanelKeyboard(this.panelKeyboardBinding);
    this.panelKeyboardBinding = null;
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {}

  getFirstError(): string {
    const errors = this.formControl.errors;

    return errors[Object.keys(errors)[0]];
  }
}
